import { devlog, devwarn } from '../../util/utils.ts';
import styleText from './as-popout-card.css?raw';
import { sendEv } from '../../data/events.ts';

/**
 * # ASPopoutCard Component
 * 
 * A card component that can be shown/hidden and positioned relative to a button.
 * 
 * ## Features
 * - Can be shown/hidden via the `show` attribute
 * - Configurable placement relative to a button via the `placement` attribute
 * - Uses CSS variables for positioning and dimensions
 * - Emits events when visibility changes
 * - Handles its own positioning based on placement
 * - Closes when clicking outside the card
 * 
 * ## Component Hierarchy
 * ```
 * ASPopoutCard (as-popout-card)
 * └── slot - For the card content
 * ```
 * 
 * ## Attributes
 * - `show`: When present, shows the card
 * - `placement`: Controls where the card appears relative to the button (default: "right")
 *   - Supported values: "right", "left", "top", "bottom", "bottom-left", "bottom-right", "top-left", "top-right"
 * 
 * ## CSS Variables
 * - `--offset-x`: Horizontal offset from the reference point (default: 0)
 * - `--offset-y`: Vertical offset from the reference point (default: 0)
 * - `--offset-x-pad`: Additional horizontal padding (default: 0px)
 * - `--gap`: Gap between the reference point and the card (default: 10px)
 * - `--min-width`: Minimum width of the card (default: 10rem)
 * - `--max-width`: Maximum width of the card (default: 32rem)
 * - `--max-height`: Maximum height of the card
 * - `--padding-global`: Padding inside the card (default: 2rem)
 * - `--shadow`: Box shadow for the card (default: 0 2px 10px rgba(0, 0, 0, 0.1))
 * - `--border-radius`: Border radius of the card (default: 1rem)
 * - `--border`: Border of the card (default: none)
 * - `--popout-background`: Background color of the card (default: rgb(250, 250, 250), fully opaque)
 * - `--position-style`: Position style of the card (default: relative)
 * 
 * ## Events
 * ### Emitted Events
 * - `as-intrn-visibility-changed`: Dispatched when the card's visibility changes
 *   - Detail: `{ visible: boolean }`
 */
export class ASPopoutCard extends HTMLElement {
  static readonly tag = 'as-popout-card' as const;

  private static readonly _sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  private _isVisible: boolean = false;
  private _placement: string = "right";
  private _gap: number = 10; // Gap between button and card in pixels
  private _outsideClickHandler: (e: MouseEvent) => void;
  private _buttonElement: HTMLElement | null = null;
  private _toggleTimeout: number | null = null;
  private _clickLock: boolean = false;
  private _clickLockTimeout: number | null = null;

  /**
   * Define the observed attributes for the component
   */
  static get observedAttributes(): string[] {
    return ['show', 'placement'];
  }

  constructor() {
    super();
    
    // Create shadow DOM with template content
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      ASPopoutCard._sheet
    ];
    this._render();
    
    // Bind the outside click handler
    this._outsideClickHandler = this._handleOutsideClick.bind(this);
  }

  /**
   * Renders the component template with current state
   */
  private _render(): void {
    this.shadowRoot!.innerHTML = /*html*/`
      <slot></slot>
    `;
  }

  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'show' && oldValue !== newValue) {
      this._isVisible = this.hasAttribute('show');
      this._updateVisibility();
    } else if (name === 'placement' && oldValue !== newValue) {
      // Validate placement value
      const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
      this._placement = validPlacements.includes(newValue) ? newValue : 'right';
      
      // Reposition the card if it's visible
      if (this._isVisible) {
        this._positionCard();
      }
    }
  }

  /**
   * Sets up event listeners when the component is connected to the DOM
   */
  connectedCallback() {
    // Set initial visibility state
    this._isVisible = this.hasAttribute('show');
    this._updateVisibility();
    
    // Set initial placement
    if (this.hasAttribute('placement')) {
      const placementAttr = this.getAttribute('placement');
      if (placementAttr) {
        const validPlacements = ['right', 'left', 'top', 'bottom', 'bottom-left', 'bottom-right', 'top-left', 'top-right'];
        this._placement = validPlacements.includes(placementAttr) ? placementAttr : 'right';
      }
    }
    
    // Listen for the as-intrn-toggle-visibility event
    this.addEventListener('as-intrn-toggle-visibility', this._handleToggleVisibility);
    
    // Find the parent button element
    this._findParentButton();
  }

  /**
   * Finds the parent button element that controls this card
   */
  private _findParentButton(): void {
    // Look for a parent element that might be the button
    let parent = this.parentElement;
    while (parent) {
      if (parent.tagName === 'AS-POPOUT-BUTTON' || 
          parent.tagName === 'AS-ICON-BUTTON' || 
          parent.tagName === 'AS-TEXT-BUTTON') {
        this._buttonElement = parent as HTMLElement;
        break;
      }
      parent = parent.parentElement;
    }
  }

  /**
   * Handles the as-intrn-toggle-visibility event
   */
  private _handleToggleVisibility = (event: Event) => {
    // Prevent event from bubbling further
    event.stopPropagation();
    
    // If we're in a click lock, ignore this toggle
    if (this._clickLock) {
      return;
    }
    
    // Debounce the toggle to prevent multiple rapid toggles
    if (this._toggleTimeout !== null) {
      window.clearTimeout(this._toggleTimeout);
    }
    
    this._toggleTimeout = window.setTimeout(() => {
      // Toggle the show attribute
      if (this.hasAttribute('show')) {
        this.removeAttribute('show');
        this._isVisible = false;
      } else {
        this.setAttribute('show', '');
        this._isVisible = true;
      }
      
      // Update visibility and dispatch event
      this._updateVisibility();
      
      // Reset the timeout
      this._toggleTimeout = null;
    }, 50); // Small delay to debounce
  }

  /**
   * Updates the visibility state and dispatches an event
   */
  private _updateVisibility(): void {
    // Position the card before showing it
    if (this._isVisible) {
      this._positionCard();
      
      // Add outside click listener when showing
      setTimeout(() => {
        document.addEventListener('mousedown', this._outsideClickHandler);
      }, 100); // Small delay to prevent immediate firing when clicking the button
    } else {
      // Remove outside click listener when hiding
      document.removeEventListener('mousedown', this._outsideClickHandler);
    }
    
    // Dispatch the as-intrn-visibility-changed event
    sendEv(this, 'as-intrn-visibility-changed', {
      detail: { visible: this._isVisible }
    });
  }

  /**
   * Handles clicks outside the card to close it
   */
  private _handleOutsideClick = (e: MouseEvent): void => {
    devlog("handleOutsideClick", e);
    
    // If the event was already handled by the popout card click handler, ignore it
    if ((e as any)._handledByPopoutCard) {
      devlog("Event was already handled by popout card, ignoring");
      return;
    }
    
    // Check if the click is outside the card and not on the button
    const isOutsideCard = !this.contains(e.target as Node);
    const isOnButton = this._buttonElement?.contains(e.target as Node);
    
    if (isOutsideCard && !isOnButton) {
      // Set click lock to prevent toggle from firing immediately after
      this._clickLock = true;
      
      // Clear any existing click lock timeout
      if (this._clickLockTimeout !== null) {
        window.clearTimeout(this._clickLockTimeout);
      }
      
      // Close the card
      this._isVisible = false;
      this.removeAttribute('show');
      this._updateVisibility();
      
      // Release the click lock after a delay
      this._clickLockTimeout = window.setTimeout(() => {
        this._clickLock = false;
        this._clickLockTimeout = null;
      }, 300); // Longer delay to ensure toggle doesn't fire
    }
  }

  /**
   * Positions the card based on the placement attribute
   */
  private _positionCard(): void {
    // Get the card dimensions
    const cardWidth = this.offsetWidth || 200; // Default width if not yet rendered
    const cardHeight = this.offsetHeight || 100; // Default height if not yet rendered
    
    // Get the gap from CSS variable or use default
    const gap = parseInt(getComputedStyle(this).getPropertyValue('--gap') || '10');
    
    // Position the card based on placement
    switch (this._placement) {
      case 'right':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) + ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
        break;
      case 'left':
        this.style.setProperty('--offset-x', `calc(-${cardWidth}px - ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
        break;
      case 'top':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      case 'bottom':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'bottom-left':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) - ${cardWidth}px)`);
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'bottom-right':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(var(--reference-height, 0px) + ${gap}px)`);
        break;
      case 'top-left':
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) - ${cardWidth}px)`);
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      case 'top-right':
        this.style.setProperty('--offset-x', '0px');
        this.style.setProperty('--offset-y', `calc(-${cardHeight}px - ${gap}px)`);
        break;
      default:
        // Default to right if placement is invalid
        this.style.setProperty('--offset-x', `calc(var(--reference-width, 0px) + ${gap}px)`);
        this.style.setProperty('--offset-y', '0px');
    }
  }

  /**
   * Clean up event listeners when the element is removed from the DOM
   */
  disconnectedCallback() {
    this.removeEventListener('as-intrn-toggle-visibility', this._handleToggleVisibility);
    document.removeEventListener('mousedown', this._outsideClickHandler);
  }
}

customElements.define(ASPopoutCard.tag, ASPopoutCard);
